import type { QueryClient } from "@tanstack/react-query";

import {
  invalidateDashboardAffectedQueries,
  isDashboardAffectedQueryKey,
} from "@/lib/query/dashboard-affected-queries";

/**
 * Request-path fragments for the resources a compliance write can change beyond
 * the dashboard queries, which `dashboard-affected-queries` always adds.
 *
 * Completing an instance cascades server-side well beyond the compliance
 * tables: it schedules the next instance, opens follow-up actions for failed
 * checklist items, writes evidence rows, and moves the dashboard status that
 * drives both the summary cards and the priority list. Reopening, rescheduling,
 * deleting and deactivating move the same surfaces.
 *
 * The Compliance Manager's key embeds a `completedFrom` derived from the current
 * time, so it can't be reconstructed from a mutation site — one of the reasons
 * the shared predicate matches on a path fragment rather than the whole key.
 */
const COMPLIANCE_AFFECTED_KEY_PATHS = [
  "/compliance-tasks/",
  "/follow-up-actions",
  "/evidence",
] as const;

/**
 * True when a React Query key belongs to a resource that a compliance write can
 * change. Exported for the drift-guard test.
 */
export function isComplianceAffectedQueryKey(
  queryKey: readonly unknown[],
): boolean {
  return isDashboardAffectedQueryKey(queryKey, COMPLIANCE_AFFECTED_KEY_PATHS);
}

/**
 * Query keys of the hand-rolled compliance hooks, which use their own array
 * keys rather than a request path and would otherwise keep serving stale
 * instances: the Workspace to-do list and the completion screen's detail view.
 */
const COMPLIANCE_LOCAL_QUERY_KEYS = [
  ["compliance-instances-list"],
  ["compliance-instance-with-definition"],
] as const;

/**
 * Invalidate every query that renders data a compliance write can change.
 */
export async function invalidateComplianceQueries(
  queryClient: QueryClient,
): Promise<void> {
  await invalidateDashboardAffectedQueries(queryClient, {
    keyPaths: COMPLIANCE_AFFECTED_KEY_PATHS,
    queryKeys: COMPLIANCE_LOCAL_QUERY_KEYS,
  });
}

export function extractApiErrorMessage(
  error: unknown,
  fallback: string,
): string {
  if (
    error &&
    typeof error === "object" &&
    "response" in error &&
    error.response &&
    typeof error.response === "object" &&
    "data" in error.response
  ) {
    const responseData = error.response.data as Record<string, unknown>;
    if (responseData.message && typeof responseData.message === "string") {
      return responseData.message;
    }
  }
  return fallback;
}
